import * as UI from "@iyk/ui"

import { Assets } from "./assets.ts"
import { Carousel } from "./carousel.tsx"

const Root = ({ children }: { children: React.ReactNode }) => {
  return <Carousel.Root> {children}</Carousel.Root>
}

const Content = () => {
  return (
    <CarouselContainer>
      <CarouselViewport>
        {slides.map((slide, index) => (
          <CarouselSlide key={index}>
            <div className="flex flex-col gap-8 select-none px-4">
              <img src={slide.image.src} alt={slide.image.alt} className="w-full" />
              <div className="flex flex-col gap-2 text-center">
                <p className="text-[9px] uppercase font-departure-mono">Step {slide.step.number}</p>
                <h2 className="text-lg">{slide.step.title}</h2>
                <p className="text-xs text-balance min-h-[2lh] max-w-72 mx-auto">
                  {slide.step.description}
                </p>
              </div>
            </div>
          </CarouselSlide>
        ))}
      </CarouselViewport>

      <CarouselDots />
    </CarouselContainer>
  )
}

export const HowItWorksCarousel = {
  Root,
  Content,
}

interface Slide {
  image: {
    src: string
    alt: string
  }
  step: {
    number: number
    title: string
    description: string
  }
}

const slides: Slide[] = [
  {
    image: {
      src: Assets.images.howItWorks.step1,
      alt: "Tap all stations",
    },
    step: {
      number: 1,
      title: "Tap all stations",
      description:
        "Locate the stations around the festival, hold your phone to the symbol to tap and check in",
    },
  },
  {
    image: {
      src: Assets.images.howItWorks.step2,
      alt: "Visit all stations to enter",
    },
    step: {
      number: 2,
      title: "Visit all stations to enter",
      description: "Check in at all 4 stations for a shot at winning a VIP upgrade + merch bundle",
    },
  },
]

const CarouselContainer = ({ children }: { children: React.ReactNode }) => {
  const { scrollSnapList } = Carousel.useCarousel()
  const showContainer = Boolean(scrollSnapList().length)

  return (
    <div
      data-show={showContainer}
      className="relative flex flex-col gap-4 opacity-0 data-[show=true]:opacity-100 transition-opacity duration-300"
    >
      {children}
    </div>
  )
}

const CarouselViewport = ({ children }: { children: React.ReactNode }) => {
  const { ref } = Carousel.useCarousel()

  return (
    <div ref={ref} className="grow flex flex-col justify-end overflow-hidden">
      <div className="grow flex -ml-0 [touch-action:pan-y_pinch-zoom]">{children}</div>
    </div>
  )
}

const CarouselSlide = ({ children }: { children: React.ReactNode }) => {
  return (
    <section
      className={UI.cx([
        "flex flex-col justify-center min-w-0 pl-0 select-none",
        "[transform:translate3d(0,0,0)]",
        "flex-[0_0_100%]",
        "transition-all duration-300 animate-in",
      ])}
    >
      {children}
    </section>
  )
}

const CarouselDots = () => {
  const { scrollSnapList, selectedScrollSnap, goTo } = Carousel.useCarousel()
  const slides = scrollSnapList()
  const selectedSlide = selectedScrollSnap()

  return (
    <div className="flex gap-2 px-4 mx-auto">
      {slides.map((_, index) => (
        <button
          type="button"
          key={index}
          data-selected={index === selectedSlide}
          onClick={() => goTo(index)}
          className={UI.cx(
            "size-1 rounded-full",
            "data-[selected=true]:bg-gray-12",
            "data-[selected=false]:border data-[selected=false]:border-gray-8",
          )}
        />
      ))}
    </div>
  )
}
